import { useState, useEffect, useMemo } from 'react'
import { detectBrand, isVariant, type BrandDef } from '../data/brands'
import { type Model, type ModelCategory, type TaskType } from '../data/models'
import { hasFrameRefModes, isKling4Model, isWan3Model } from '../utils/frameRefModes'

const API_BASE = import.meta.env.VITE_API_URL || ''

/** Ограничения модели из БД (price_variants.constraints). Известные поля
 *  типизированы, остальное — произвольный JSON, поэтому индекс unknown. */
export interface ModelConstraints {
  /** Пропорции. В БД встречаются ОБА имени: `allowed_aspects` заполнено у
   *  шести моделей (Sora ×3, Veo 3.1 ×3), `allowed_aspect_ratios` — у одной.
   *  Читаем оба: у Sora из-за этого расхождения выбора пропорций не было
   *  вовсе, хотя ограничение в базе лежало (замер 13.09). */
  allowed_aspects?: string[]
  allowed_aspect_ratios?: string[]
  allowed_durations?: (string | number)[]
  allowed_resolutions?: string[]
  [key: string]: unknown
}

/** Сценарий использования модели: чип + объяснение простыми словами. */
export interface ModelScenario {
  key: string
  label: string
  detail: string
}

export interface APIVariant {
  key: string
  label: string
  credits_price: number | null
  is_per_second?: boolean
  per_input_image?: boolean
}

export interface APIModel {
  id: string
  display_name: string
  type: string
  pricing: { input_per_1k: number; output_per_1k: number }
  credits_price: number | null
  is_enabled: boolean
  variants: APIVariant[] | null
  min_credits_price: number | null
  constraints: ModelConstraints | null
  ui_metadata: Record<string, unknown> | null
  /** Для чего пригодна модель — выводит бэк из её реальных возможностей. */
  scenarios?: ModelScenario[] | null
  /** Длительность посекундно (Seedance): [мин, макс] секунд из кода адаптера, суженные списком админа. */
  duration_range?: number[] | null
  /** «Как встанет персонаж» — вердикт резолвера персонажей (шаг 8 бэка). */
  character_fit?: { mode: string; count?: number; warnings?: string[] } | null
  /** Плата за секунды входного видео включена у этой модели (backend services/input_video_billing). */
  input_video_billing?: boolean
}

export interface APIProvider {
  name: string
  display_name: string
  type: string
  models: APIModel[]
}

export interface BrandModel {
  id: string
  displayName: string
  type: string
  adapter: string
  creditsPrice: number | null
  variants: APIVariant[] | null
  minCreditsPrice: number | null
  constraints: ModelConstraints | null
  uiMetadata: Record<string, unknown> | null
  scenarios: ModelScenario[] | null
  characterFit: { mode: string; count?: number; warnings?: string[] } | null
  /** Длительность посекундно (Seedance): [мин, макс] секунд из кода адаптера. */
  duration_range: number[] | null
  /** Плата за секунды входного видео (APIModel.input_video_billing). */
  inputVideoBilling?: boolean
}


export interface Brand extends BrandDef {
  models: BrandModel[]
  modelCount: number
}

interface ModelMeta {
  aspectRatios?: string[]
  resolutions?: string[]
  durations?: string[]
  maxImages?: number
  supportsImageInput?: boolean
  requiresImage?: boolean
  requiresVideo?: boolean
  supportsReferenceVideo?: boolean
  supportsNegativePrompt?: boolean
  supportsOutputFormat?: boolean
  outputFormats?: string[]
  supportsGoogleSearch?: boolean
  supportsPromptOptimizer?: boolean
  requiresTwoImages?: boolean
  qualityLevels?: string[]
  supportsMultiShot?: boolean
  supportsEndImage?: boolean
  supportsLastFrame?: boolean
  supportsAudio?: boolean
  supportsReferenceAudio?: boolean
  maxReferenceAudios?: number
  supportsReferenceVideoIntent?: boolean
  promptRefStyle?: 'seedance' | 'kling' | 'wan'
  maxReferenceVideos?: number
  minReferenceVideoSeconds?: number
  maxReferenceVideoSeconds?: number
  maxReferenceAudioSeconds?: number
  imageLimits?: { minPx: number; maxPx: number; maxRatio: number }
  referenceVideoFormats?: string[]
  referenceAudioFormats?: string[]
  referenceVideoMaxMb?: number
  referenceAudioMaxMb?: number
  /** Продление сцены (Gemini Omni Original) — см. Model.supportsSceneExtend. */
  supportsSceneExtend?: boolean
  maxSceneSeconds?: number
  paramHints?: Record<string, { label?: string; description?: string }>
}

const MODEL_DISPLAY: Record<string, { name: string; desc: string }> = {
  'gpt-6-astra': { name: 'ChatGPT 6 Astra', desc: 'Флагманская модель OpenAI шестого поколения: максимум в сложных задачах и размышлении.' },
  'gpt-6.1-terra': { name: 'ChatGPT 6.1 Terra', desc: 'Новая модель OpenAI: уровень GPT-6 вдвое дешевле Sol — для повседневной работы, кода и агентных задач.' },
  'gpt-6-sol': { name: 'ChatGPT 6 Sol', desc: 'Шестое поколение OpenAI по цене вдвое ниже: сильная модель для кодинга, анализа и агентных задач.' },
  'gpt-6-luna': { name: 'ChatGPT 6 Luna', desc: 'Самая быстрая и экономичная модель GPT-6 для повседневных задач.' },
  'gpt-5.6-sol': { name: 'ChatGPT 5.6 Sol', desc: 'Флагман OpenAI нового поколения: максимум для кодинга, ресёрча и агентных задач.' },
  'gpt-5.6-terra': { name: 'ChatGPT 5.6 Terra', desc: 'Баланс качества, скорости и цены для повседневной работы.' },
  'gpt-5.6-luna': { name: 'ChatGPT 5.6 Luna', desc: 'Самая быстрая и экономичная модель семейства 5.6.' },
  'gpt-5.5': { name: 'ChatGPT 5.5', desc: 'Новый флагман OpenAI для сложного reasoning, кодинга и агентных задач.' },
  'gpt-5.5-pro': { name: 'ChatGPT 5.5 Pro', desc: 'Премиальная версия с максимальной глубиной рассуждения.' },
  'chat-latest': { name: 'ChatGPT 5.5 Instant', desc: 'Быстрая версия ChatGPT 5.5 с пониженным уровнем галлюцинаций.' },
  'gpt-5.4': { name: 'ChatGPT 5.4', desc: 'Флагманская модель OpenAI с нативным компьютерным управлением.' },
  'gpt-5.4-pro': { name: 'ChatGPT 5.4 Pro', desc: 'Максимальная производительность для сложных профессиональных задач.' },
  'gpt-5.4-mini': { name: 'ChatGPT 5.4 Mini', desc: 'Компактная и быстрая модель для повседневных задач.' },
  'gpt-5.4-nano': { name: 'ChatGPT 5.4 Nano', desc: 'Самая экономичная модель серии 5.4.' },
  'gpt-5.2': { name: 'ChatGPT 5.2', desc: 'Мощная универсальная модель для текста, кода, аналитики и сложных задач.' },
  'gpt-5.2-chat-latest': { name: 'ChatGPT 5.2', desc: 'Мощная универсальная модель для текста, кода, аналитики и сложных задач.' },
  'gpt-5.2-pro': { name: 'ChatGPT 5.2 Pro', desc: 'Продвинутая версия с повышенной точностью и глубиной рассуждений.' },
  'gpt-5.1': { name: 'ChatGPT 5.1', desc: 'Мощная текстовая модель от OpenAI.' },
  'gpt-5': { name: 'ChatGPT 5', desc: 'Текстовая модель нового поколения от OpenAI.' },
  'gpt-5-mini': { name: 'ChatGPT 5 Mini', desc: 'Компактная и быстрая модель для повседневных задач.' },
  'gpt-5-nano': { name: 'ChatGPT 5 Nano', desc: 'Сверхбыстрая и экономичная модель.' },
  'gpt-4.1': { name: 'ChatGPT 4.1', desc: 'Надёжная текстовая модель от OpenAI.' },
  'gpt-4.1-mini': { name: 'ChatGPT 4.1 Mini', desc: 'Быстрая и экономичная модель для повседневных задач.' },
  'gpt-4.1-nano': { name: 'ChatGPT 4.1 Nano', desc: 'Сверхбыстрая и экономичная модель.' },
  'gpt-4o': { name: 'ChatGPT 4o', desc: 'Мультимодальная модель от OpenAI.' },
  'gpt-4o-mini': { name: 'ChatGPT 4o Mini', desc: 'Быстрая мультимодальная модель.' },
  'o3': { name: 'o3', desc: 'Модель глубокого рассуждения от OpenAI.' },
  'o3-mini': { name: 'o3 Mini', desc: 'Компактная модель рассуждения.' },
  'o4-mini': { name: 'o4 Mini', desc: 'Быстрая модель рассуждения нового поколения.' },
  'gpt-4-turbo': { name: 'ChatGPT 4 Turbo', desc: 'Быстрая версия GPT-4.' },
  'gpt-3.5-turbo': { name: 'ChatGPT 3.5 Turbo', desc: 'Классическая быстрая модель.' },
  'claude-opus-4-5-20251101': { name: 'Claude Opus 4.5', desc: 'Глубокая аналитика и точные формулировки для сложных текстов.' },
  'claude-sonnet-4-5-20250929': { name: 'Claude Sonnet 4.5', desc: 'Баланс скорости и качества для регулярной работы.' },
  'claude-haiku-4-5-20251001': { name: 'Claude Haiku 4.5', desc: 'Сверхбыстрые ответы для простых задач.' },
  'claude-opus-4-1-20250805': { name: 'Claude Opus 4.1', desc: 'Глубокая аналитика от Anthropic.' },
  'claude-sonnet-4-20250514': { name: 'Claude Sonnet 4', desc: 'Быстрая и качественная модель от Anthropic.' },
  'claude-sonnet-4-5-latest': { name: 'Claude Sonnet 4.5', desc: 'Баланс скорости и качества для регулярной работы.' },
  'claude-haiku-4-5-latest': { name: 'Claude Haiku 4.5', desc: 'Сверхбыстрые ответы для простых задач.' },
  'claude-opus-5-5': { name: 'Claude Opus 5.5', desc: 'Новейший Opus: сильнее и дешевле Opus 5 — глубокое рассуждение, сложный кодинг и длительные агентные задачи.' },
  'claude-opus-5': { name: 'Claude Opus 5', desc: 'Новое поколение Opus: глубокое рассуждение, сложный кодинг и длительные агентные задачи.' },
  'claude-opus-4-8': { name: 'Claude Opus 4.8', desc: 'Флагман Anthropic для кодинга и долгосрочных агентных задач.' },
  'claude-fable-5': { name: 'Claude Fable 5', desc: 'Флагман Anthropic класса Mythos, мощнее Opus — для самых сложных и длительных задач: масштабный кодинг, исследования, глубокий анализ.' },
  'claude-fable-5-1': { name: 'Claude Fable 5.1', desc: 'Новейшая модель Anthropic класса Mythos: ещё сильнее в долгих агентных задачах, многошаговых исследованиях и работе с документами, таблицами и презентациями.' },
  'claude-sonnet-5-5': { name: 'Claude Sonnet 5.5', desc: 'Новейший Sonnet: быстрее и сильнее Sonnet 5 в кодинге, агентных задачах и повседневной работе.' },
  'claude-sonnet-5': { name: 'Claude Sonnet 5', desc: 'Мощная модель для кодинга, агентов и повседневной работы. Баланс качества и стоимости.' },
  'claude-opus-4-7': { name: 'Claude Opus 4.7', desc: 'Новый флагман Anthropic для кодинга и долгосрочных агентных задач.' },
  'claude-opus-4-6': { name: 'Claude Opus 4.6', desc: 'Премиальная модель для сложных задач и глубокого анализа.' },
  'deepseek-v4-pro': { name: 'DeepSeek V4 Pro', desc: 'Флагман с reasoning и контекстом 1M токенов.' },
  'deepseek-v4-flash': { name: 'DeepSeek V4 Flash', desc: 'Быстрая и экономичная модель серии V4.' },
  'deepseek-chat': { name: 'DeepSeek V3.2 Chat', desc: 'Быстрая текстовая модель для диалога.' },
  'deepseek-reasoner': { name: 'DeepSeek Reasoner', desc: 'Модель глубокого рассуждения на базе V4 Pro.' },
  'nano-banana-pro': { name: 'Nano Banana Pro', desc: 'Продвинутая генерация изображений с высокой детализацией.' },
  'google/nano-banana-pro': { name: 'Nano Banana Pro', desc: 'Продвинутая генерация изображений с высокой детализацией.' },
  'google/nano-banana': { name: 'Nano Banana Standard', desc: 'Быстрая и стабильная генерация изображений.' },
  'google/nano-banana-edit': { name: 'Nano Banana Edit', desc: 'Редактирование и улучшение готовых изображений.' },
  'nano-banana-2': { name: 'Nano Banana 2', desc: 'Быстрая генерация изображений с Pro-качеством до 2K и поиском Google.' },
  'nano-banana-2-1': { name: 'Nano Banana 2.1', desc: 'Новая модель Google: точнее следует промпту, лучше держит лица и текст, до 4K.' },
  'mj_txt2img': { name: 'Midjourney (Text to Image)', desc: 'Создание художественных изображений по описанию.' },
  'mj_img2img': { name: 'Midjourney (Image to Image)', desc: 'Переработка и стилизация изображений.' },
  'mj_video': { name: 'Midjourney (Image to Video)', desc: 'Анимация изображений в короткие видеоролики.' },
  'flux-2/pro-text-to-image': { name: 'Flux 2 Pro', desc: 'Премиальная генерация детализированных изображений.' },
  'flux-2/pro-image-to-image': { name: 'Flux 2 Pro (Image to Image)', desc: 'Редактирование и улучшение изображений по образцу.' },
  'flux-2/flex-text-to-image': { name: 'Flux 2 Flex', desc: 'Гибкая генерация с высокой скоростью.' },
  'flux-2/flex-image-to-image': { name: 'Flux 2 Flex (Image to Image)', desc: 'Изменение и доработка существующих изображений.' },
  'black-forest-labs/flux-pro': { name: 'Flux Pro', desc: 'Качественная генерация для креативных проектов.' },
  'black-forest-labs/flux-schnell': { name: 'Flux Schnell', desc: 'Максимальная скорость генерации.' },
  'black-forest-labs/flux-dev': { name: 'Flux Dev', desc: 'Экспериментальная версия для продвинутых пользователей.' },
  // Flux 3 (20.10): Klein — KIE (adapters/flux), Image — Replicate (новый роутинг, app/scripts/seed_flux3).
  'flux-3/klein-text-to-image': { name: 'Flux 3 Klein', desc: 'Быстрая и недорогая Flux 3: генерация и правка по 10 фото, до 4K.' },
  'flux-3-klein': { name: 'Flux 3 Klein', desc: 'Быстрая и недорогая Flux 3: генерация и правка по 10 фото, до 4K.' },
  'black-forest-labs/flux-3-image': { name: 'Flux 3 Image', desc: 'Флагманская Flux 3: точное следование промпту, правка по 10 фото, до 4K.' },
  'flux-3-image': { name: 'Flux 3 Image', desc: 'Флагманская Flux 3: точное следование промпту, правка по 10 фото, до 4K.' },
  'google/imagen-4': { name: 'Imagen 4', desc: 'Реалистичная генерация изображений от Google.' },
  'google/imagen-4-fast': { name: 'Imagen 4 Fast', desc: 'Быстрая версия для моментального результата.' },
  'google/imagen-4-ultra': { name: 'Imagen 4 Ultra', desc: 'Максимальное качество и фотореализм.' },
  'stability-ai/stable-diffusion-3.5-large': { name: 'Stable Diffusion 3.5 Large', desc: 'Гибкая генерация изображений с широкими настройками.' },
  'stability-ai/stable-diffusion-3.5-large-turbo': { name: 'SD 3.5 Large Turbo', desc: 'Ускоренная версия для быстрой работы.' },
  'cdingram/face-swap': { name: 'Face Swap', desc: 'Замена лица на изображениях с высокой точностью.' },
  'runwayml/gen4-image': { name: 'Runway Gen-4 Image', desc: 'Генерация изображений и креативных сцен.' },
  'runwayml/gen4-image-turbo': { name: 'Runway Gen-4 Image Turbo', desc: 'Ускоренная генерация изображений.' },
  'gen4-turbo': { name: 'Runway Gen-4 Turbo', desc: 'Быстрое создание видео и визуальных сцен.' },
  'runwayml/gen4-turbo': { name: 'Runway Gen-4 Turbo', desc: 'Быстрое создание видео и визуальных сцен.' },
  'kling-2.6/text-to-video': { name: 'Kling 2.6 (Text to Video)', desc: 'Создание видео по текстовому описанию.' },
  'kling-2.6/image-to-video': { name: 'Kling 2.6 (Image to Video)', desc: 'Анимация изображений в видео.' },
  'kling-2.6/motion-control': { name: 'Kling 2.6 Motion Control', desc: 'Генерация видео с управлением движением камеры.' },
  'kling-3.0': { name: 'Kling 3.0', desc: 'Кинематографичная генерация видео: мультишот, нативное аудио, до 15 секунд.' },
  'kling-3.0/video': { name: 'Kling 3.0', desc: 'Кинематографичная генерация видео: мультишот, нативное аудио, до 15 секунд.' },
  // ui_metadata из БД спредится ПОВЕРХ этой записи — имя карточки правится
  // в model_provider_prices.ui_metadata.name, а не только здесь.
  'kling-3.0-direct': { name: 'Kling 3.0 (Original)', desc: 'Kling 3.0 напрямую от разработчика: без очередей и ограничений посредника.' },
  'kling-v3-omni-direct': { name: 'Kling V3 Omni (Original)', desc: 'Мультимодальный Kling Omni напрямую: фото, видео и аудио на вход, звук в результате.' },
  'kwaivgi/kling-v2.6-motion-control': { name: 'Kling 2.6 Motion Control', desc: 'Генерация видео с управлением движением камеры.' },
  'kwaivgi/kling-v3-motion-control': { name: 'Kling 3.0 Motion Control', desc: 'Перенос движения с видео на изображение, улучшенное качество.' },
  'wan-video/wan-2.2-animate-replace': { name: 'Wan 2.2 Animate Replace', desc: 'Замена человека в ролике: аватар с фото встаёт на его место — фон, камера, свет и звук из ролика.' },
  'prunaai/p-video-replace': { name: 'P-Video Replace', desc: 'Замена человека в ролике: аватар с фото встаёт на его место — движение, камера, сцена и звук из ролика.' },
  'hailuo/02-text-to-video-standard': { name: 'Hailuo 02', desc: 'Качественная генерация коротких AI-видео.' },
  'hailuo/02-text-to-video-pro': { name: 'Hailuo 02 Pro', desc: 'Премиальное качество AI-видео.' },
  'hailuo/02-image-to-video-standard': { name: 'Hailuo 02 (Image to Video)', desc: 'Создание видео на основе изображения.' },
  'hailuo/02-image-to-video-pro': { name: 'Hailuo 02 Pro (Image to Video)', desc: 'Премиальное видео из изображения.' },
  'hailuo/02-text-to-video-fast': { name: 'Hailuo 02 Fast', desc: 'Ускоренная генерация видео.' },
  'minimax/hailuo-02-fast': { name: 'Hailuo 02 Fast', desc: 'Ускоренная генерация видео.' },
  'openai/sora-2': { name: 'Sora 2', desc: 'Продвинутая генерация реалистичных видео от OpenAI.' },
  'openai/sora-2-pro': { name: 'Sora 2 Pro', desc: 'Премиальное качество и высокая детализация видео.' },
  'sora-2-text-to-video': { name: 'Sora 2', desc: 'Продвинутая генерация реалистичных видео от OpenAI.' },
  'sora-2-image-to-video': { name: 'Sora 2 (Image to Video)', desc: 'Создание видео из изображения.' },
  'sora-2-pro-text-to-video': { name: 'Sora 2 Pro', desc: 'Премиальное качество и высокая детализация видео.' },
  'sora-2-pro-image-to-video': { name: 'Sora 2 Pro (Image to Video)', desc: 'Премиальное видео из изображения.' },
  'bytedance/seedance-1-pro': { name: 'Seedance 1.5 Pro', desc: 'Генерация динамичных AI-видео.' },
  'bytedance/seedance-1-pro-fast': { name: 'Seedance 1.5 Pro Fast', desc: 'Быстрая генерация динамичных видео.' },
  'bytedance/seedance-1-lite': { name: 'Seedance 1 Lite', desc: 'Лёгкая и быстрая генерация видео.' },
  'bytedance/seedance-1.5-pro': { name: 'Seedance 1.5 Pro', desc: 'Генерация динамичных AI-видео.' },
  // Luma Ray: пропорции ровно по схеме Replicate (сверка 14.09) — 3:2 и 2:3
  // там нет, Replicate отвечал бы 422. Два фото: первый и последний кадр.
  'luma/ray': { name: 'Luma Ray', desc: 'Генерация видео от Luma.' },
  'luma/ray-flash-2-540p': { name: 'Luma Ray Flash 2 (540P)', desc: 'Скоростная генерация коротких видео' },
  'luma/photon-flash': { name: 'Luma Photon Flash', desc: 'Генерация изображений от Luma AI' },
  'veo3.1_fast': { name: 'Veo 3.1 Fast', desc: 'Быстрая генерация видео от Google.' },
  'veo3.1_quality': { name: 'Veo 3.1 Quality', desc: 'Высокое качество видео от Google.' },
  'minimax/image-01': { name: 'MiniMax Image', desc: 'Генерация изображений от MiniMax.' },
  'kwaivgi/kling-v3-omni-video': { name: 'Kling V3 Omni', desc: 'Мультимодальная генерация видео с аудио, до 15 сек 1080p' },
  'gamma-presentation': { name: 'Gamma AI', desc: 'Создание новых презентаций с профессиональным дизайном и AI-изображениями.' },
  'umnik-slides': { name: 'Umnik AI', desc: 'AI-помощник для презентаций. Загрузите PPTX и опишите изменения.' },
  'suno-v4_5all': { name: 'Suno V4.5 All', desc: 'Генерация музыки с хорошей структурой, треки до 4 минут.' },
  'suno-v5': { name: 'Suno V5', desc: 'Улучшенное качество звука и проработки.' },
  'suno-v5_5': { name: 'Suno V5.5', desc: 'Максимальное качество и персонализация голоса.' },
  'suno-v6': { name: 'Suno V6', desc: 'Новое поколение Suno: живой вокал, богаче аранжировка, точно следует стилю.' },
  'suno-v6_wild': { name: 'Suno V6 Wild', desc: 'Смелее и непредсказуемее V6 — для экспериментов и необычного звучания.' },
  'suno-v6_mini': { name: 'Suno V6 Mini', desc: 'Лёгкая и быстрая версия V6.' },
  'mureka-8': { name: 'Mureka 8', desc: 'Сбалансированная модель Mureka для вокальных треков.' },
  'mureka-9': { name: 'Mureka 9', desc: 'Флагман Mureka — максимальное качество звука.' },
  'mureka-o2': { name: 'Mureka o2 (reasoning)', desc: 'Модель Mureka с reasoning для сложных композиций.' },
  'mureka-7.6': { name: 'Mureka 7.6', desc: 'Быстрая модель Mureka для генерации музыки.' },
  'auto': { name: 'Mureka Auto', desc: 'Mureka автоматически подбирает оптимальную модель.' },
  'elevenlabs-v4': { name: 'ElevenLabs v4', desc: 'Озвучка текста живыми голосами: русский и ещё 90+ языков, интонации и эмоции.' },
  'elevenlabs-multilingual-v2': { name: 'ElevenLabs Multilingual v2', desc: 'Стабильная озвучка на 29 языках, ровный голос на длинных текстах.' },
  'elevenlabs-flash-v2.5': { name: 'ElevenLabs Flash v2.5', desc: 'Быстрая и недорогая озвучка на 32 языках.' },
  'minimax/hailuo-2.3-fast': { name: 'Hailuo 2.3 Fast', desc: 'Быстрая генерация видео от MiniMax.' },
  'runwayml/gen4-aleph': { name: 'Runway Gen4 Aleph', desc: 'Продвинутая генерация видео от Runway.' },
  'black-forest-labs/flux-1.1-pro-ultra': { name: 'Flux 1.1 Pro Ultra', desc: 'Максимальное качество генерации изображений.' },
  'stability-ai/stable-diffusion-3.5-medium': { name: 'Stable Diffusion 3.5 Medium', desc: 'Сбалансированная генерация изображений.' },
  'luma/ray-flash-2-720p': { name: 'Luma Ray Flash 2 720P', desc: 'Быстрая генерация видео 720p.' },
  'luma/ray-2-720p': { name: 'Luma Ray 2 720P', desc: 'Генерация видео 720p от Luma AI.' },
  'bytedance/seedance-1.5-standard': { name: 'Seedance 1.5 Standard', desc: 'Стандартная генерация видео от ByteDance.' },
  'bytedance/v1-lite-image-to-video': { name: 'Seedance Lite (Image to Video)', desc: 'Лёгкая анимация изображений в видео.' },
  'claude-sonnet-4-6': { name: 'Claude Sonnet 4.6', desc: 'Быстрая и точная модель от Anthropic.' },
  'flux-1.1-pro-ultra': { name: 'Flux 1.1 Pro Ultra', desc: 'Максимальное качество генерации изображений.' },
  'flux-2-flex': { name: 'Flux 2 Flex', desc: 'Гибкая генерация с высокой скоростью.' },
  'flux-2-pro': { name: 'Flux 2 Pro', desc: 'Премиальная генерация детализированных изображений.' },
  'flux-kontext/pro-image-to-image': { name: 'Flux Kontext Pro (Image to Image)', desc: 'Контекстное редактирование изображений.' },
  'flux-kontext/pro-text-to-image': { name: 'Flux Kontext Pro', desc: 'Контекстная генерация изображений по описанию.' },
  'gemini-2.0-flash': { name: 'Gemini 2.0 Flash', desc: 'Быстрая модель от Google.' },
  'gemini-2.0-flash-001': { name: 'Gemini 2.0 Flash', desc: 'Быстрая модель от Google.' },
  'gemini-2.0-flash-lite-001': { name: 'Gemini 2.0 Flash Lite', desc: 'Сверхбыстрая облегчённая модель Google.' },
  'gemini-2.5-flash': { name: 'Gemini 2.5 Flash', desc: 'Быстрая и умная модель от Google.' },
  'gemini-2.5-flash-lite': { name: 'Gemini 2.5 Flash Lite', desc: 'Облегчённая версия для простых задач.' },
  'gemini-2.5-flash-preview-05-20': { name: 'Gemini 2.5 Flash Preview', desc: 'Предварительная версия Gemini 2.5 Flash.' },
  'gemini-2.5-pro': { name: 'Gemini 2.5 Pro', desc: 'Мощная профессиональная модель от Google.' },
  'gemini-2.5-pro-preview-05-06': { name: 'Gemini 2.5 Pro Preview', desc: 'Предварительная версия Gemini 2.5 Pro.' },
  'gemini-3-flash-preview': { name: 'Gemini 3 Flash Preview', desc: 'Быстрая модель нового поколения от Google.' },
  'gemini-3-pro-preview': { name: 'Gemini 3 Pro Preview', desc: 'Профессиональная модель нового поколения от Google.' },
  'gemini-3.1-pro-preview': { name: 'Gemini 3.1 Pro', desc: 'Флагман Google с расширенным reasoning и контекстом 1M токенов.' },
  'gpt-5.3-chat-latest': { name: 'ChatGPT 5.3', desc: 'Мощная текстовая модель от OpenAI.' },
  'grok-2-vision-1212': { name: 'Grok 2 Vision', desc: 'Мультимодальная модель с анализом изображений.' },
  'grok-3': { name: 'Grok 3', desc: 'Мощная текстовая модель от xAI.' },
  'grok-3-mini': { name: 'Grok 3 Mini', desc: 'Компактная и быстрая модель от xAI.' },
  'grok-4.7': { name: 'Grok 4.7', desc: 'Новый флагман xAI с reasoning и анализом изображений.' },
  'grok-4.3': { name: 'Grok 4.3', desc: 'Новый флагман xAI с reasoning и видео-вводом.' },
  'grok-4.20-0309-non-reasoning': { name: 'Grok 4.20', desc: 'Мульти-агентная модель xAI в стандартном режиме.' },
  'grok-4.20-0309-reasoning': { name: 'Grok 4.20 Reasoning', desc: 'Мульти-агентная модель xAI с глубоким рассуждением.' },
  'grok-4-0709': { name: 'Grok 4', desc: 'Флагманская модель от xAI.' },
  'grok-4-1-fast-non-reasoning': { name: 'Grok 4.1 Fast', desc: 'Быстрая модель без рассуждения от xAI.' },
  'grok-4-1-fast-reasoning': { name: 'Grok 4.1 Fast Reasoning', desc: 'Быстрая модель с рассуждением от xAI.' },
  'grok-4-fast-non-reasoning': { name: 'Grok 4 Fast', desc: 'Быстрая модель без рассуждения от xAI.' },
  'grok-4-fast-reasoning': { name: 'Grok 4 Fast Reasoning', desc: 'Быстрая модель с рассуждением от xAI.' },
  'grok-code-fast-1': { name: 'Grok Code Fast', desc: 'Специализированная модель для кода от xAI.' },
  'hailuo-02-i2v': { name: 'Hailuo 02 I2V', desc: 'Создание видео на основе изображения.' },
  'hailuo-02-i2v-pro': { name: 'Hailuo 02 I2V Pro', desc: 'Премиальное видео из изображения.' },
  'hailuo-02-i2v-standard': { name: 'Hailuo 02 I2V', desc: 'Создание видео на основе изображения.' },
  'hailuo-02-t2v-pro': { name: 'Hailuo 02 T2V Pro', desc: 'Премиальное качество AI-видео.' },
  'hailuo-02-t2v-standard': { name: 'Hailuo 02 T2V', desc: 'Качественная генерация коротких AI-видео.' },
  'hailuo/2-3-image-to-video-pro': { name: 'Hailuo 2.3 I2V Pro', desc: 'Премиальное видео из изображения.' },
  'hailuo/2-3-image-to-video-standard': { name: 'Hailuo 2.3 I2V', desc: 'Создание видео на основе изображения.' },
  'kling-2.6-i2v': { name: 'Kling 2.6 Image to Video', desc: 'Анимация изображений в видео.' },
  'kling-2.6-motion': { name: 'Kling 2.6 Motion', desc: 'Генерация видео с управлением движением.' },
  'kling-2.6-t2v': { name: 'Kling 2.6 Text to Video', desc: 'Создание видео по текстовому описанию.' },
  'kling-v3-motion': { name: 'Kling V3 Motion', desc: 'Перенос движения с видео на изображение.' },
  'kling/ai-avatar-pro': { name: 'Kling AI Avatar Pro', desc: 'Создание реалистичных AI-аватаров.' },
  'kling/ai-avatar-standard': { name: 'Kling AI Avatar', desc: 'Генерация AI-аватаров.' },
  'kling/v2-1-master-image-to-video': { name: 'Kling 2.1 Master I2V', desc: 'Анимация изображений в видео высшего качества.' },
  'kling/v2-1-master-text-to-video': { name: 'Kling 2.1 Master T2V', desc: 'Создание видео высшего качества по описанию.' },
  'kling/v2-1-pro': { name: 'Kling 2.1 Pro', desc: 'Продвинутая генерация видео от Kling.' },
  'kling/v2-1-standard': { name: 'Kling 2.1 Standard', desc: 'Стандартная генерация видео от Kling.' },
  'kwaivgi/kling-v2.6': { name: 'Kling 2.6', desc: 'Генерация видео от Kling.' },
  'kwaivgi/kling-v3-video': { name: 'Kling V3 Video', desc: 'Генерация видео нового поколения от Kling.' },
  'luma-photon-flash': { name: 'Luma Photon Flash', desc: 'Быстрая генерация изображений от Luma AI.' },
  'luma-ray': { name: 'Luma Ray', desc: 'Генерация видео от Luma AI.' },
  'luma-ray-2-720p': { name: 'Luma Ray 2 720P', desc: 'Генерация видео 720p от Luma AI.' },
  'luma-ray-flash-2-540p': { name: 'Luma Ray Flash 2 540P', desc: 'Быстрая генерация видео 540p.' },
  'luma-ray-flash-2-720p': { name: 'Luma Ray Flash 2 720P', desc: 'Быстрая генерация видео 720p.' },
  'midjourney': { name: 'Midjourney', desc: 'Художественная генерация изображений.' },
  'minimax-image': { name: 'MiniMax Image', desc: 'Генерация изображений от MiniMax.' },
  'minimax-speech-hd': { name: 'MiniMax Speech HD', desc: 'Озвучка текста в HD-качестве.' },
  'minimax-speech-turbo': { name: 'MiniMax Speech Turbo', desc: 'Быстрая озвучка текста.' },
  'minimax-video': { name: 'MiniMax Video', desc: 'Генерация видео от MiniMax.' },
  'minimax/hailuo-02': { name: 'Hailuo 02', desc: 'Качественная генерация AI-видео.' },
  'minimax/hailuo-2.3': { name: 'Hailuo 2.3', desc: 'Улучшенная генерация AI-видео.' },
  'minimax/video-01': { name: 'MiniMax Video 01', desc: 'Генерация видео от MiniMax.' },
  'runway-gen4-aleph': { name: 'Runway Gen4 Aleph', desc: 'Продвинутая генерация видео от Runway.' },
  'runway-gen4-image': { name: 'Runway Gen4 Image', desc: 'Генерация изображений от Runway.' },
  'runway-gen4-image-turbo': { name: 'Runway Gen-4 Image Turbo', desc: 'Ускоренная генерация изображений от Runway.' },
  'runway-gen4-turbo': { name: 'Runway Gen4 Turbo', desc: 'Быстрое создание видео от Runway.' },
  'runwayml-gen4-turbo': { name: 'Runway Gen4 Turbo', desc: 'Быстрое создание видео от Runway.' },
  'sd-3.5-large': { name: 'Stable Diffusion 3.5 Large', desc: 'Гибкая генерация с широкими настройками.' },
  'sd-3.5-large-turbo': { name: 'Stable Diffusion 3.5 Large Turbo', desc: 'Ускоренная генерация изображений.' },
  'sd-3.5-medium': { name: 'Stable Diffusion 3.5 Medium', desc: 'Сбалансированная генерация изображений.' },
  'seedance-lite': { name: 'Seedance Lite', desc: 'Лёгкая и быстрая генерация видео.' },
  'seedance-pro': { name: 'Seedance Pro', desc: 'Генерация динамичных AI-видео.' },
  'seedance-pro-fast': { name: 'Seedance Pro Fast', desc: 'Быстрая генерация динамичных видео.' },
  'sora-2': { name: 'Sora 2', desc: 'Генерация реалистичных видео от OpenAI.' },
  'sora-2-pro': { name: 'Sora 2 Pro', desc: 'Премиальное качество видео от OpenAI.' },
  'sora-2-pro-hd': { name: 'Sora 2 Pro HD', desc: 'Видео высокой чёткости от OpenAI.' },
  'gpt-image-2.5-sunburst': { name: 'GPT Image 2.5 Sunburst', desc: 'Флагман OpenAI для картинок: максимальное качество и точное редактирование.' },
  'gpt-image-2.5-flare': { name: 'GPT Image 2.5 Flare', desc: 'Быстрая версия GPT Image 2.5: то же качество вдвое быстрее.' },
  'gpt-image-2': { name: 'GPT Image 2', desc: 'Новейшая модель OpenAI с идеальным текстом и фотореализмом.' },
  'veo-3.1-lite': { name: 'Veo 3.1 Lite', desc: 'Бюджетная генерация видео с нативным аудио от Google.' },
  'veo-3.1-fast': { name: 'Veo 3.1 Fast', desc: 'Быстрая генерация видео с аудио от Google.' },
  'google/veo-3.1-lite': { name: 'Veo 3.1 Lite', desc: 'Бюджетная генерация видео с нативным аудио от Google.' },
  'google/veo-3.1-fast': { name: 'Veo 3.1 Fast', desc: 'Быстрая генерация видео с аудио от Google.' },
  'google/veo-3.1': { name: 'Veo 3.1', desc: 'Премиальное видео от Google с референсами и аудио.' },
  'z-image-turbo': { name: 'Z-Image Turbo', desc: 'Сверхбыстрая генерация фотореалистичных изображений.' },
  'prunaai/z-image-turbo': { name: 'Z-Image Turbo', desc: 'Сверхбыстрая генерация фотореалистичных изображений.' },
  'seedance-2.0': { name: 'Seedance 2.0', desc: 'Мультимодальная генерация видео с аудио от ByteDance.' },
  'bytedance/seedance-2.0': { name: 'Seedance 2.0', desc: 'Мультимодальная генерация видео с аудио от ByteDance.' },
  'bytedance/seedance-2': { name: 'Seedance 2.0', desc: 'Мультимодальная генерация видео с аудио от ByteDance.' },
  'seedance-2.0-fast': { name: 'Seedance 2.0 Fast', desc: 'Быстрая генерация видео от ByteDance.' },
  'bytedance/seedance-2.0-fast': { name: 'Seedance 2.0 Fast', desc: 'Быстрая генерация видео от ByteDance.' },
  'bytedance/seedance-2-fast': { name: 'Seedance 2.0 Fast', desc: 'Быстрая генерация видео от ByteDance.' },
  'seedance-2.5': { name: 'Seedance 2.5', desc: 'Видео до 30 секунд со звуком, до 30 референсов, редактирование и продление роликов.' },
  'bytedance/seedance-2.5': { name: 'Seedance 2.5', desc: 'Видео до 30 секунд со звуком, до 30 референсов, редактирование и продление роликов.' },
  'bytedance/seedance-2-5': { name: 'Seedance 2.5', desc: 'Видео до 30 секунд со звуком, до 30 референсов, редактирование и продление роликов.' },
  // Wan 3.0 (KIE, 19.10): обычная и быстрая Prime — adapters/wan.
  'wan/3-0-video': { name: 'Wan 3.0', desc: 'Видео до 30 секунд со звуком: текст, первый и последний кадр, до 10 фото-референсов и аудио, до 1080p.' },
  'wan/3-0-video-prime': { name: 'Wan 3.0 Prime', desc: 'Быстрая версия Wan 3.0: видео до 30 секунд со звуком, кадры и до 10 фото-референсов, до 1080p.' },
  // Kling 4.0 (KIE, 26.10): одна карточка, кадры и референсы — две модели KIE (adapters/kling4).
  'kling-4.0/reference-to-video': { name: 'Kling 4.0', desc: 'Видео до 30 секунд со звуком: текст, первый и последний кадр или до 10 фото- и видео-референсов, до 1080p.' },
  'gemini-omni-video': { name: 'Gemini Omni', desc: 'Мультимодальная видеогенерация Google: видео со звуком из текста и референсов, до 4k.' },
  'gemini-omni-video-direct': { name: 'Gemini Omni (Original)', desc: 'Gemini Omni напрямую от Google: без ограничений посредника, до 4k со звуком.' },
  'seedream-4.5': { name: 'Seedream 4.5', desc: 'Кинематографические изображения с точным следованием промпту.' },
  'bytedance/seedream-4.5': { name: 'Seedream 4.5', desc: 'Кинематографические изображения с точным следованием промпту.' },
  'seedream-5-lite': { name: 'Seedream 5 Lite', desc: 'Генерация изображений с reasoning и example-based editing.' },
  'bytedance/seedream-5-lite': { name: 'Seedream 5 Lite', desc: 'Генерация изображений с reasoning и example-based editing.' },
  'animagine-xl-v4': { name: 'Animagine XL v4', desc: 'Специализированная модель для аниме-арта.' },
  'aisha-ai-official/animagine-xl-v4-opt': { name: 'Animagine XL v4', desc: 'Специализированная модель для аниме-арта.' },
  'heygen-video-agent': { name: 'HeyGen Video Agent', desc: 'AI создаёт полное видео: сценарий, аватар, озвучка, монтаж.' },
  'heygen/video-agent': { name: 'HeyGen Video Agent', desc: 'AI создаёт полное видео: сценарий, аватар, озвучка, монтаж.' },
  'heygen-avatar-iv': { name: 'HeyGen Avatar IV', desc: 'Реалистичный говорящий аватар из текста.' },
  'heygen/avatar-iv': { name: 'HeyGen Avatar IV', desc: 'Реалистичный говорящий аватар из текста.' },
  'heygen-video-translate': { name: 'HeyGen Video Translate', desc: 'Перевод видео на 150+ языков с сохранением голоса.' },
  'heygen/video-translate': { name: 'HeyGen Video Translate', desc: 'Перевод видео на 150+ языков с сохранением голоса.' },
}

const MODEL_METADATA: Record<string, ModelMeta> = {
  'flux-2/pro-text-to-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['1K', '2K'], supportsImageInput: true },
  'flux-2/pro-image-to-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['1K', '2K'], requiresImage: true },
  'flux-2/flex-text-to-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['1K', '2K'], supportsImageInput: true },
  'flux-2/flex-image-to-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['1K', '2K'], requiresImage: true },
  // Short-form id'ы (model_name из model_provider_prices). Фолбэк на случай,
  // когда модель приходит в /providers через bare model_settings-loop с
  // ui_metadata=null (например активна Replicate-строка без ui_metadata) —
  // тогда пилюли AR/разрешения берутся отсюда. При корректной KIE-конфигурации
  // ui_metadata из БД перекрывает эти значения.
  'flux-2-pro': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['1K', '2K'], supportsImageInput: true },
  'flux-2-flex': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['1K', '2K'], supportsImageInput: true },
  'flux-kontext-pro': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['1K', '2K'], supportsImageInput: true },
  // Flux 3: «auto» — по первому фото (без фото Klein рисует 1:1, Image подбирает по промпту); до 10 фото.
  'flux-3/klein-text-to-image': { aspectRatios: ['auto', '1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9', '9:21'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, maxImages: 10 },
  'flux-3-klein': { aspectRatios: ['auto', '1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9', '9:21'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, maxImages: 10 },
  'black-forest-labs/flux-3-image': { aspectRatios: ['auto', '1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9', '9:21'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, maxImages: 10 },
  'flux-3-image': { aspectRatios: ['auto', '1:1', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9', '9:21'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, maxImages: 10 },
  'nano-banana-pro': { aspectRatios: ['match_input_image', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, supportsOutputFormat: true },
  'google/nano-banana-pro': { aspectRatios: ['match_input_image', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, supportsOutputFormat: true },
  'nano-banana-pro-i2i': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'], resolutions: ['1K', '2K'] },
  'google/nano-banana': { aspectRatios: ['match_input_image', '1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'], supportsImageInput: true, supportsOutputFormat: true },
  'google/nano-banana-edit': { aspectRatios: ['1:1', '9:16', '16:9', '3:4', '4:3', '3:2', '2:3'], requiresImage: true },
  'nano-banana-2': { aspectRatios: ['match_input_image', '1:1', '1:4', '1:8', '2:3', '3:2', '3:4', '4:1', '4:3', '4:5', '5:4', '8:1', '9:16', '16:9', '21:9'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, supportsOutputFormat: true, supportsGoogleSearch: true },
  'nano-banana-2-1': { aspectRatios: ['match_input_image', '1:1', '1:4', '1:8', '2:3', '3:2', '3:4', '4:1', '4:3', '4:5', '5:4', '8:1', '9:16', '16:9', '21:9'], resolutions: ['1K', '2K', '4K'], supportsImageInput: true, supportsOutputFormat: true, maxImages: 10 },
  'midjourney/text-to-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] },
  'midjourney/image-to-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], requiresImage: true },
  'midjourney/image-to-video': { aspectRatios: ['16:9', '9:16'], requiresImage: true },
  'mj_txt2img': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] },
  'mj_img2img': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], requiresImage: true },
  'mj_video': { aspectRatios: ['16:9', '9:16'], requiresImage: true },
  'black-forest-labs/flux-pro': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], supportsImageInput: true },
  // Ultra принимает только jpg/png: webp из общего набора пилюли Replicate отвергает, генерация падает (замер 26.09).
  // Кода-возможностей у модели нет (её нет в ReplicateAdapter.MODELS) — список держим здесь.
  'black-forest-labs/flux-1.1-pro-ultra': { supportsOutputFormat: true, outputFormats: ['jpg', 'png'] },
  'flux-1.1-pro-ultra': { supportsOutputFormat: true, outputFormats: ['jpg', 'png'] },
  'black-forest-labs/flux-dev': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], supportsImageInput: true },
  'black-forest-labs/flux-schnell': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] },
  'google/imagen-4': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] },
  'google/imagen-4-fast': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] },
  'google/imagen-4-ultra': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'] },
  'stability-ai/stable-diffusion-3.5-large': { aspectRatios: ['1:1', '16:9', '3:2', '2:3', '4:5', '5:4', '9:16', '21:9', '9:21'], supportsImageInput: true, supportsNegativePrompt: true, supportsOutputFormat: true },
  'stability-ai/stable-diffusion-3.5-large-turbo': { aspectRatios: ['1:1', '16:9', '3:2', '2:3', '4:5', '5:4', '9:16', '21:9', '9:21'], supportsImageInput: true, supportsNegativePrompt: true, supportsOutputFormat: true },
  'minimax/image-01': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4'], supportsImageInput: true },
  'runwayml/gen4-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['720p', '1080p'], supportsImageInput: true },
  'runway-gen4-image': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['720p', '1080p'], supportsImageInput: true },
  'runwayml/gen4-image-turbo': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['720p', '1080p'], requiresImage: true },
  'runway-gen4-image-turbo': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3'], resolutions: ['720p', '1080p'], requiresImage: true },
  'cdingram/face-swap': { requiresImage: true, requiresTwoImages: true },
  'hailuo/02-text-to-video-standard': { durations: ['6', '10'], supportsPromptOptimizer: true },
  'hailuo/02-text-to-video-pro': { durations: ['6', '10'], supportsPromptOptimizer: true },
  'hailuo/02-image-to-video-standard': { durations: ['6', '10'], requiresImage: true, supportsPromptOptimizer: true },
  'hailuo/02-image-to-video-pro': { durations: ['6', '10'], requiresImage: true, supportsPromptOptimizer: true },
  'minimax/hailuo-02-fast': { durations: ['6', '10'], requiresImage: true, supportsPromptOptimizer: true },
  'kling-2.6/text-to-video': { aspectRatios: ['1:1', '16:9', '9:16'], durations: ['5', '10'] },
  'kling-2.6/image-to-video': { durations: ['5', '10'], requiresImage: true },
  'kling-2.6/motion-control': { resolutions: ['720p', '1080p'], requiresImage: true, requiresVideo: true },
  'kling-3.0/video': {
    aspectRatios: ['16:9', '9:16', '1:1'],
    durations: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
    resolutions: ['720p', '1080p', '2160p'],
    supportsImageInput: true,
    supportsEndImage: true,
    supportsMultiShot: true,
    paramHints: {
      image: { description: '1 фото → первый кадр. Добавьте последний кадр для перехода.' },
    },
  },
  'kling-3.0': {
    aspectRatios: ['16:9', '9:16', '1:1'],
    durations: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
    resolutions: ['720p', '1080p', '2160p'],
    supportsImageInput: true,
    supportsEndImage: true,
    supportsMultiShot: true,
    paramHints: {
      image: { description: '1 фото → первый кадр. Добавьте последний кадр для перехода.' },
    },
  },
  // Прямой Kling (официальный API Kuaishou). Отличия от KIE-версии: 5/10 c
  // вместо 3–15 и нет мультишота — у прямого API этих параметров нет (Ф0).
  'kling-3.0-direct': {
    aspectRatios: ['16:9', '9:16', '1:1'],
    durations: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
    resolutions: ['720p', '1080p', '2160p'],
    supportsImageInput: true,
    supportsEndImage: true,
    supportsMultiShot: true,
    paramHints: {
      // У API Kling 3.0 нет референсов — фото только кадр. Говорим это в
      // подсказке, чтобы не искали слот референса (тестировщик 14.09).
      image: { description: '1 фото → первый кадр. Добавьте последний кадр для перехода. Референсы (аватар, стиль) — только у Kling V3 Omni.' },
    },
  },
  // Omni напрямую: мультимодальный вход. 4K у неё не заявлен, поэтому
  // разрешений два, а не три, как у 3.0.
  'kling-v3-omni-direct': {
    aspectRatios: ['16:9', '9:16', '1:1'],
    durations: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
    resolutions: ['720p', '1080p', '2160p'],
    supportsImageInput: true,
    supportsEndImage: true,
    supportsReferenceVideo: true,
    maxReferenceVideos: 1,
    minReferenceVideoSeconds: 3,
    supportsMultiShot: true,
    paramHints: {
      image: { description: '1 фото → первый кадр. Добавьте последний кадр для перехода.' },
      // Без видео на входе Kling принимает до 7 картинок, с видео — до 4.
      // Пороги Kling (Omni Video Generation): картинки ≥300 px по сторонам,
      // видео 700…4553 px, не больше 3840×2160 в кадре, 3…15.5 с. Сервер
      // проверяет их до постановки задачи (kling_media_check) — подсказка
      // нужна, чтобы отказ не был неожиданностью.
      // Формат в подсказке не называем: сам Kling берёт только JPG/PNG, но
      // webp (в нём лежат фото персонажа) сервер перекладывает в JPEG сам.
      reference_images: { description: 'До 7 фото-референсов: аватары, стиль, предметы (с видео — до 4). Стороны от 300 px.' },
      reference_videos: { description: 'Видео от 3 секунд (длиннее 15 обрезается), 700…3840 px по сторонам. Что с ним делать — пилюля «Видео-референс»: «Стиль и камера» или «Редактирование».' },
      videoReferenceType: { description: '«Стиль и камера» — новый ролик по движению и съёмке референса, звук выключается. «Редактирование» — правка самого ролика по инструкции (замена лица, цвета, объектов): результат той же длины и формата, что видео; кадры первый/последний не сочетаются.' },
    },
  },
  'kwaivgi/kling-v3-motion-control': { resolutions: ['720p', '1080p'], requiresImage: true, requiresVideo: true },
  'wan-video/wan-2.2-animate-replace': { resolutions: ['480p', '720p'], requiresImage: true, requiresVideo: true },
  'prunaai/p-video-replace': { resolutions: ['720p', '1080p'], requiresImage: true, requiresVideo: true },
  'kwaivgi/kling-v2.6-motion-control': { resolutions: ['720p', '1080p'], requiresImage: true, requiresVideo: true },
  'openai/sora-2': { aspectRatios: ['16:9', '9:16'], durations: ['4', '8', '12'], supportsImageInput: true },
  'openai/sora-2-pro': { aspectRatios: ['16:9', '9:16'], durations: ['4', '8', '12'], resolutions: ['720p', '1080p'], supportsImageInput: true },
  'bytedance/seedance-1.5-pro': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'], durations: ['4', '8', '12'], resolutions: ['480p', '720p', '1080p'] },
  'gen4-turbo': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'], durations: ['5', '10'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true },
  'runwayml/gen4-turbo': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'], durations: ['5', '10'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true },
  // Aleph — video-to-video. Схема Replicate (сверка 14.09): video (обязателен,
  // берутся первые 5 с), prompt, aspect_ratio, reference_image, seed —
  // длительности у модели НЕТ, цена плоская. requiresVideo обязателен: слот
  // видео рисуется только по нему (`supportsVideoInput` из ui_metadata и
  // `accepts` адаптера фронт не читает) — без него модель было нечем
  // запустить. Kling-пилюли (ориентация, звук исходника) сюда не приезжают:
  // они гейтятся на motionControl, а не на requiresVideo. Обе формы id, как у
  // пар gen4-image/gen4-turbo выше: в каталоге модель заведена дважды.
  'runwayml/gen4-aleph': { aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1', '21:9'], maxImages: 1, supportsImageInput: true, requiresVideo: true },
  'runway-gen4-aleph': { aspectRatios: ['16:9', '9:16', '4:3', '3:4', '1:1', '21:9'], maxImages: 1, supportsImageInput: true, requiresVideo: true },
  'luma/ray': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'], durations: ['5', '9'], supportsImageInput: true, maxImages: 2 },
  'luma/ray-flash-2-540p': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'], durations: ['5', '9'], supportsImageInput: true, maxImages: 2 },
  // DB-only модели Luma (могут приходить в dash-форме из model_provider_prices)
  'luma-ray-2-720p': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'], durations: ['5', '9'], supportsImageInput: true, maxImages: 2 },
  'luma-ray-flash-2-720p': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'], durations: ['5', '9'], supportsImageInput: true, maxImages: 2 },
  'luma/ray-2-720p': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'], durations: ['5', '9'], supportsImageInput: true, maxImages: 2 },
  'luma/ray-flash-2-720p': { aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'], durations: ['5', '9'], supportsImageInput: true, maxImages: 2 },
  // Photon: разрешения в схеме Replicate нет — пилюля 1K/2K была холостой.
  // Три фото: референс, стиль, персонаж (см. paramHints в ui_metadata).
  'luma/photon-flash': { aspectRatios: ['1:1', '16:9', '9:16', '4:3', '3:4', '21:9', '9:21'], supportsImageInput: true, maxImages: 3 },
  'gpt-image-2': { aspectRatios: ['auto', '1:1', '5:4', '4:5', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9'], supportsOutputFormat: true, supportsImageInput: true, maxImages: 16, qualityLevels: ['low', 'medium', 'high'] },
  'gpt-image-2.5-sunburst': { aspectRatios: ['auto', '1:1', '5:4', '4:5', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9'], supportsOutputFormat: true, supportsImageInput: true, maxImages: 16, qualityLevels: ['low', 'medium', 'high', 'xhigh', 'max'] },
  'gpt-image-2.5-flare': { aspectRatios: ['auto', '1:1', '5:4', '4:5', '4:3', '3:4', '3:2', '2:3', '16:9', '9:16', '21:9'], supportsOutputFormat: true, supportsImageInput: true, maxImages: 16, qualityLevels: ['low', 'medium', 'high', 'xhigh', 'max'] },
  'veo-3.1-lite': { aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8'], resolutions: ['720p', '1080p'], supportsImageInput: true },
  'veo-3.1-fast': { aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8'], resolutions: ['720p', '1080p'], supportsImageInput: true, supportsNegativePrompt: true },
  'veo-3.1': { aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8'], resolutions: ['720p', '1080p'], supportsImageInput: true, supportsNegativePrompt: true },
  'google/veo-3.1-lite': { aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8'], resolutions: ['720p', '1080p'], supportsImageInput: true },
  'google/veo-3.1-fast': { aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8'], resolutions: ['720p', '1080p'], supportsImageInput: true, supportsNegativePrompt: true },
  'google/veo-3.1': { aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8'], resolutions: ['720p', '1080p'], supportsImageInput: true, supportsNegativePrompt: true },
  'z-image-turbo': { aspectRatios: ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16'], supportsOutputFormat: true },
  'prunaai/z-image-turbo': { aspectRatios: ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16'], supportsOutputFormat: true },
  'seedance-2.0': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'], durations: ['5', '7', '10', '15'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, maxImages: 9 },
  'bytedance/seedance-2.0': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'], durations: ['5', '7', '10', '15'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, maxImages: 9 },
  'bytedance/seedance-2': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'], durations: ['4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, maxImages: 9 },
  'seedance-2.0-fast': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'], durations: ['5', '7', '10', '15'], resolutions: ['480p', '720p'], supportsImageInput: true, maxImages: 9 },
  'bytedance/seedance-2.0-fast': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'], durations: ['5', '7', '10', '15'], resolutions: ['480p', '720p'], supportsImageInput: true, maxImages: 9 },
  'bytedance/seedance-2-fast': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'], durations: ['4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'], resolutions: ['480p', '720p'], supportsImageInput: true, maxImages: 9 },
  'seedance-2.5': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9', 'adaptive'], durations: ['5', '8', '10', '15', '20', '25', '30'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, supportsReferenceVideo: true, maxImages: 30 },
  'bytedance/seedance-2.5': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9', 'adaptive'], durations: ['5', '8', '10', '15', '20', '25', '30'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, supportsReferenceVideo: true, maxImages: 30 },
  'bytedance/seedance-2-5': { aspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9', 'adaptive'], durations: ['5', '8', '10', '15', '20', '25', '30'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, supportsReferenceVideo: true, maxImages: 30 },
  // Gemini Omni (KIE gemini-omni-video): T2V+I2V со звуком. supportsReferenceImages/
  // supportsImageInput приходят из ui_metadata (БД); здесь — aspectRatios/durations/
  // resolutions для пилл. 1:1 модель НЕ поддерживает.
  // Wan 3.0: кадры и референсы — режимы «Кадры | Референсы» (utils/frameRefModes); видео-референсы — только с платой за
  // секунды входа (brandModelToModel ниже, backend services/wan_refs).
  // Секунды (2–30) приходят диапазоном из кода адаптера (duration_range) — посекундный слайдер, как у Seedance 2.
  'wan/3-0-video': { aspectRatios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, supportsAudio: true, supportsReferenceAudio: true, maxReferenceAudios: 5, maxImages: 10 },
  'wan/3-0-video-prime': { aspectRatios: ['adaptive', '16:9', '4:3', '1:1', '3:4', '9:16'], resolutions: ['480p', '720p', '1080p'], supportsImageInput: true, supportsAudio: true, supportsReferenceAudio: true, maxReferenceAudios: 5, maxImages: 10 },
  // Kling 4.0: кадры и референсы — режимы «Кадры | Референсы» (utils/frameRefModes), секунды 3–30 — диапазоном из кода
  // адаптера. Звук родной и всегда включён (переключателя у модели нет); видео — только при плате за секунды входа
  // (brandModelToModel ниже), голоса и субъекты пока не подключены.
  'kling-4.0/reference-to-video': { aspectRatios: ['auto', '16:9', '9:16', '1:1', '21:9'], resolutions: ['720p', '1080p'], supportsImageInput: true, maxImages: 10 },
  'gemini-omni-video': { aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8', '10'], resolutions: ['720p', '1080p', '4k'], supportsImageInput: true, supportsReferenceVideo: true },
  // Прямая версия (Gemini Developer API, gemini-omni-1.1-flash). Отличия от
  // KIE-версии: видео-референсов до трёх (адаптер заливает их в Files API),
  // есть кадры (supportsEndImage) и продление сцены до 40 c.
  'gemini-omni-video-direct': {
    aspectRatios: ['16:9', '9:16'], durations: ['4', '6', '8', '10'],
    resolutions: ['720p', '1080p', '4k'],
    supportsImageInput: true,
    supportsReferenceVideo: true, maxReferenceVideos: 3,
    supportsEndImage: true,
    supportsSceneExtend: true, maxSceneSeconds: 40,
    // Фото по умолчанию — референсы (так канал работает с 13.08). В кадры они
    // превращаются, только когда добавлен последний кадр: тогда первое фото
    // становится <FIRST_FRAME>, а «конец видео» — <LAST_FRAME>. Пишем это в
    // подсказки, иначе правило видно лишь в коде адаптера.
    paramHints: {
      image: { description: 'Фото — референсы. Добавьте «Последний кадр», и первое фото станет первым кадром перехода.' },
      end_image: { description: 'Последний кадр перехода. Первым станет первое фото выше.' },
      reference_images: { description: 'До 6 фото-референсов: аватары, стиль, предметы.' },
      reference_videos: { description: 'До 3 видео-референсов, по 3 секунды — длиннее обрежем сами.' },
    },
  },
  'seedream-4.5': { aspectRatios: ['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '21:9'], supportsImageInput: true, maxImages: 14 },
  'bytedance/seedream-4.5': { aspectRatios: ['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '21:9'], supportsImageInput: true, maxImages: 14 },
  'seedream-5-lite': { aspectRatios: ['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '21:9'], supportsImageInput: true, maxImages: 14, supportsOutputFormat: true },
  'bytedance/seedream-5-lite': { aspectRatios: ['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '21:9'], supportsImageInput: true, maxImages: 14, supportsOutputFormat: true },
  'animagine-xl-v4': { aspectRatios: ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16'], supportsNegativePrompt: true },
  'aisha-ai-official/animagine-xl-v4-opt': { aspectRatios: ['1:1', '3:2', '2:3', '4:3', '3:4', '16:9', '9:16'], supportsNegativePrompt: true },
  // HeyGen video-agent: prompt → видео, AI сам выбирает аватара. Длительность —
  // target в секундах, передаётся как duration_sec. Aspect ratio мапится в
  // orientation (landscape/portrait) на backend'е.
  'heygen-video-agent': { aspectRatios: ['16:9', '9:16'], durations: ['5', '10', '15', '30', '45', '60'] },
  'heygen/video-agent': { aspectRatios: ['16:9', '9:16'], durations: ['5', '10', '15', '30', '45', '60'] },
  // HeyGen avatar-iv: avatar_id+voice_id+text → говорящее видео. Aspect ratio
  // мапится в width/height (1920x1080 / 1080x1920 / 1080x1080) в адаптере.
  // Никаких upload — avatar/voice выбираются из списков через picker'ы.
  'heygen-avatar-iv': { aspectRatios: ['16:9', '9:16'] },
  'heygen/avatar-iv': { aspectRatios: ['16:9', '9:16'] },
  // HeyGen video-translate: принимает video URL или upload mp4. requiresVideo
  // → стандартный UploadThumb для файла. Aspect ratio не имеет смысла (видео
  // уже задано), durations тоже — translate сохраняет длительность исходника.
  'heygen-video-translate': { requiresVideo: true },
  'heygen/video-translate': { requiresVideo: true },
}

let cachedBrands: Brand[] | null = null

let cachedCatalog: Record<string, CatalogEntry> | null = null
// Возможности моделей из кода адаптеров: ключ `провайдер/модель`.
let cachedCodeCaps: Record<string, Record<string, unknown>> = {}
let cachedFilters: ApiFilter[] | null = null
let cachedAssignments: ApiAssignment[] | null = null
let cachedTools: ApiTool[] | null = null

// Запрос «в полёте». Модульного кэша выше НЕ хватало: он заполняется только
// когда ответ ПРИШЁЛ, а useProviders зовут ~35 компонентов и монтируются они
// одновременно — каждый успевал стартовать свою пару fetch'ей. Замер прода
// 2026-07-31: /providers ×4 по 76 КБ и /admin/public/catalog ×4 по 90 КБ,
// ~600 КБ лишнего трафика на первом экране. Теперь параллельные вызовы ждут
// ОДНО обещание; после ответа их обслуживает кэш.
let inflightProviders: Promise<void> | null = null

interface CatalogEntry {
  custom_description: string | null
  widget_description: string | null
  icon_url: string | null
  sort_order: number
  widget_sort_order: number
  is_visible_page: boolean
  is_visible_widget: boolean
}

interface ApiFilter {
  id: string
  parent_id: string | null
  name: string
  slug: string
  level: number
  sort_order: number
  is_active: boolean
  is_visible_page: boolean
  is_visible_widget: boolean
}

interface ApiAssignment {
  filter_id: string
  provider: string
  model_id: string
}

/** Карточка-инструмент каталога (бэк: catalog_tools, отдаётся в /public/catalog). */
export interface ApiTool {
  id: string
  slug: string
  title: string
  description: string | null
  widget_description: string | null
  icon_url: string | null
  category: string
  target_kind: string
  target_ref: string | null
  target_url: string
  price_label: string | null
  sort_order: number
  widget_sort_order: number
  is_active: boolean
  is_visible_page: boolean
  is_visible_widget: boolean
}

/** Карточка-инструмент → Model-подобный объект витрины.
 *
 *  provider='tool', id=slug: так карточка бесплатно проезжает через уже
 *  существующие поиск, группировку по категориям и ключи `provider:model_id`
 *  для привязки к подфильтрам L2. Рейтинг/пользователей НЕ выдумываем (у моделей
 *  они синтетические от stableHash) — ModelCard для инструмента их не рисует.
 */
export function toolToModel(t: ApiTool, variant: 'page' | 'widget'): Model {
  const desc = variant === 'widget' ? (t.widget_description || t.description || '') : (t.description || '')
  const order = variant === 'widget' ? t.widget_sort_order : t.sort_order
  return {
    id: t.slug,
    provider: 'tool',
    name: t.title,
    shortName: '',
    description: desc,
    category: (t.category as Model['category']) || 'image',
    taskType: 'text',
    cost: 0,
    rating: 0,
    users: 0,
    icon: t.icon_url || '',
    color: '#6366f1',
    backendModel: t.slug,
    isTool: true,
    toolUrl: t.target_url,
    priceLabel: t.price_label || '',
    showcaseOrder: order ?? 9999,
  }
}

function groupByBrand(providers: APIProvider[]): Brand[] {
  const brandMap = new Map<string, Brand>()
  const seen = new Set<string>()

  for (const provider of providers) {
    for (const model of provider.models) {
      if (isVariant(model.id)) continue

      const def = detectBrand(provider.name, model.id)
      if (!def) continue

      const dedupeKey = `${def.id}:${model.id}`
      if (seen.has(dedupeKey)) continue
      seen.add(dedupeKey)

      if (!brandMap.has(def.id)) {
        brandMap.set(def.id, { ...def, models: [], modelCount: 0 })
      }

      const brand = brandMap.get(def.id)!
      brand.models.push({
        id: model.id,
        displayName: model.display_name,
        type: model.type,
        adapter: provider.name,
        creditsPrice: model.credits_price,
        variants: model.variants || null,
        minCreditsPrice: model.min_credits_price ?? null,
        constraints: model.constraints || null,
        uiMetadata: model.ui_metadata ?? null,
        scenarios: model.scenarios ?? null,
        duration_range: Array.isArray(model.duration_range) ? model.duration_range : null,
        characterFit: model.character_fit ?? null,
        inputVideoBilling: model.input_video_billing === true,
      })
      brand.modelCount = brand.models.length
    }
  }

  const order: Record<string, number> = { text: 0, image: 1, video: 2, audio: 3, mixed: 4 }
  return Array.from(brandMap.values()).sort((a, b) => {
    const d = (order[a.category] ?? 9) - (order[b.category] ?? 9)
    return d !== 0 ? d : b.modelCount - a.modelCount
  })
}

/** Все целые секунды диапазона [мин, макс] строками; null — диапазона нет. */
/** [мин, макс] по списку секунд из кода адаптера; пусто или мусор — null. */
function providerRange(list: unknown): [number, number] | null {
  if (!Array.isArray(list) || list.length === 0) return null
  const nums = list.map(v => Number(v)).filter(n => Number.isFinite(n) && n > 0)
  if (!nums.length) return null
  return [Math.min(...nums), Math.max(...nums)]
}

function secondsList(range: number[] | null | undefined): string[] | null {
  if (!Array.isArray(range) || range.length !== 2) return null
  const lo = Math.round(Number(range[0]))
  const hi = Math.round(Number(range[1]))
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo <= 0 || lo > hi) return null
  return Array.from({ length: hi - lo + 1 }, (_, i) => String(lo + i))
}

function stableHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

/** Запасные лимиты референсов Seedance (docs KIE, 25.09) — САМЫЙ НИЖНИЙ слой: пока бэк
 *  с /providers/capabilities не выкачен или ответ не догрузился, виджет не должен снова
 *  пугать «максимум 15 сек» у 2.5 (лимит там 30). Код адаптера, приехав, перекрывает. */
const SEEDANCE_20_LIMITS = { promptRefStyle: 'seedance' as const,
  maxReferenceVideoSeconds: 15, minReferenceVideoSeconds: 2, maxReferenceAudioSeconds: 15,
  referenceVideoFormats: ['mp4', 'mov'], referenceAudioFormats: ['mp3', 'wav'],
  referenceVideoMaxMb: 50, referenceAudioMaxMb: 15,
  imageLimits: { minPx: 300, maxPx: 6000, maxRatio: 2.5 },
}
const SEEDANCE_25_LIMITS = { ...SEEDANCE_20_LIMITS, maxReferenceVideoSeconds: 30, maxReferenceAudioSeconds: 30, referenceVideoMaxMb: 200, supportsReferenceVideoIntent: true }
const WAN_30_LIMITS = { promptRefStyle: 'wan' as const,
  maxReferenceAudioSeconds: 15, referenceAudioFormats: ['mp3', 'wav'], referenceAudioMaxMb: 15,
  maxReferenceVideoSeconds: 15, minReferenceVideoSeconds: 1, referenceVideoFormats: ['mp4', 'mov'], referenceVideoMaxMb: 100,
  imageLimits: { minPx: 240, maxPx: 8000, maxRatio: 8 },
}
const KLING_40_LIMITS = { promptRefStyle: 'kling' as const,
  imageLimits: { minPx: 300, maxPx: 65536, maxRatio: 2.5 },
  maxReferenceVideoSeconds: 30, minReferenceVideoSeconds: 2, referenceVideoFormats: ['mp4', 'mov'],
}
export const REFERENCE_LIMIT_FALLBACKS: Record<string, Partial<Model>> = {
  'bytedance/seedance-2': SEEDANCE_20_LIMITS,
  'bytedance/seedance-2-fast': SEEDANCE_20_LIMITS,
  'bytedance/seedance-2-5': SEEDANCE_25_LIMITS,
  'bytedance/seedance-2.5': SEEDANCE_25_LIMITS,
  'seedance-2.5': SEEDANCE_25_LIMITS,
  // Wan 3.0 (docs KIE, 19.10): аудио-референсы до 15 с суммарно, фото 240–8000 px, не вытянутее 8:1; видео (26.10) —
  // до 5 роликов по 1–15 с, вместе до 15 с, mp4/mov до 100 МБ.
  'wan/3-0-video': WAN_30_LIMITS,
  'wan/3-0-video-prime': WAN_30_LIMITS,
  // Kling 4.0 (docs KIE, 26.10): ссылки @image_1 / @video_1, фото 300–65536 px, соотношение 0,4–2,5; ролики по 2–30 с,
  // вместе держим в 30 с (backend services/kling4_refs).
  'kling-4.0/reference-to-video': KLING_40_LIMITS,
}

export function brandModelToModel(brand: Brand, bm: BrandModel): Model {
  const cat: ModelCategory = bm.type === 'image' ? 'image' : bm.type === 'video' ? 'video' : bm.type === 'audio' ? 'audio' : bm.type === 'presentation' ? 'presentation' : 'text'
  const task: TaskType = cat === 'text' || cat === 'presentation' ? 'text' : cat === 'image' ? 't2i' : cat === 'audio' ? 't2a' : 't2v'
  const meta = MODEL_METADATA[bm.id]

  // Фильтруем null/undefined значения из uiMetadata чтобы они не
  // перезаписывали brand.icon и другие поля установленные выше.
  // Без этого ui_metadata с icon: null затирала иконку бренда (Seedream 4.5).
  const safeUiMeta: Record<string, unknown> = {}
  if (bm.uiMetadata) {
    for (const [k, v] of Object.entries(bm.uiMetadata)) {
      if (v != null && v !== '') safeUiMeta[k] = v
    }
  }

  // ⚠️ Core identity fields (id, provider, backendModel) MUST go AFTER
  // the meta/uiMetadata spreads. ui_metadata приходит из БД
  // (model_provider_prices.ui_metadata) и может содержать произвольные
  // ключи, включая "id" — если спред стоит позже, он перезапишет
  // идентификатор модели, и findModelById перестанет её находить
  // (баг: дефолтная модель из welcome-config не появляется на главной).
  return {
    name: MODEL_DISPLAY[bm.id]?.name || bm.displayName || bm.id,
    shortName: '',
    description: MODEL_DISPLAY[bm.id]?.desc || brand.description,
    category: cat,
    taskType: task,
    cost: bm.id.startsWith('suno-') ? (bm.creditsPrice ?? 0) : (bm.minCreditsPrice ?? bm.creditsPrice ?? 0),
    // defaultCost = «основная» цена из БД (credits_price). НЕ min из variants
    // как у `cost`. Используется computeModelCost когда settings НЕ матчат variant —
    // fallback на реальный default вместо самого дешёвого варианта (см. modelCost.ts).
    defaultCost: bm.creditsPrice ?? bm.minCreditsPrice ?? 0,
    rating: parseFloat((4.5 + (stableHash(bm.id) % 50) / 100).toFixed(2)),
    users: 15000 + (stableHash(bm.id + 'u') % 285000),
    icon: brand.icon,
    color: '#6366f1',
    // Порядок слоёв снизу вверх: код адаптера → таблица MODEL_CAPS → ui_metadata
    // из БД. Ручные метаданные и наша таблица всегда перебивают код.
    ...(REFERENCE_LIMIT_FALLBACKS[bm.id] || {}),
    ...(cachedCodeCaps[`${bm.adapter}/${bm.id}`] || {}),
    ...(meta || {}),
    ...safeUiMeta,
    ...(bm.constraints?.allowed_aspect_ratios || bm.constraints?.allowed_aspects
      ? { aspectRatios: bm.constraints.allowed_aspect_ratios || bm.constraints.allowed_aspects }
      : {}),
    ...(bm.constraints?.allowed_durations ? { durations: bm.constraints.allowed_durations.map(String) } : {}),
    // Посекундная модель: список секунд — весь диапазон из кода (уже суженный админом), слайдер
    // виджета (durationSlider при >5 значений) получает верные края, а не редкие пресеты.
    ...(secondsList(bm.duration_range) ? { durations: secondsList(bm.duration_range)! } : {}),
    ...(bm.constraints?.allowed_resolutions ? { resolutions: bm.constraints.allowed_resolutions } : {}),
    variants: bm.variants?.map(v => ({ key: v.key, label: v.label, credits_price: v.credits_price, is_per_second: v.is_per_second, per_input_image: v.per_input_image })) || undefined,
    ...(bm.inputVideoBilling ? { inputVideoBilling: true } : {}),
    // Разрешения из строк цен — по возрастанию: первый элемент становится
    // дефолтом настроек (useGenerateSettings) и обязан быть самым дешёвым, а в
    // БД строки лежат как заведены (у Gen4 Image — 1080p раньше 720p).
    ...(bm.variants?.some(v => /^\d+p$/i.test(v.key))
      ? { resolutions: bm.variants.filter(v => /^\d+p$/i.test(v.key)).map(v => v.key).sort((a, b) => parseInt(a, 10) - parseInt(b, 10)) }
      : {}),
    ...(bm.scenarios?.length ? { scenarios: bm.scenarios } : {}),
    ...(bm.characterFit ? { characterFit: bm.characterFit } : {}),
    // Диапазон секунд самой модели — из кода адаптера (/providers/capabilities), НЕ суженный
    // списком админа: по нему сервер режет длину референса в «Переделать видео» (seedance_refs),
    // и пилюля с ценой должны резать так же. Слайдер по-прежнему берёт суженный список.
    ...(providerRange(cachedCodeCaps[`${bm.adapter}/${bm.id}`]?.durations) ? { providerDurationRange: providerRange(cachedCodeCaps[`${bm.adapter}/${bm.id}`]?.durations)! } : {}),
    // Seedance 2: фото-референсы виджет шлёт из ОСНОВНОГО фото-слота (режим «Референсы», buildVideoPayload) —
    // отдельного omni-канала reference_image_urls у него нет. Код адаптера флаг ставит (он верен для Мастерской),
    // но в виджете он рисовал мёртвый второй ряд «Ваше фото» мимо лимита карточки и уводил фото персонажа в канал,
    // который запрос Seedance отбрасывает (владелец 28.09). Условие — то же, что isSeedance2 у сборки запроса.
    ...(hasFrameRefModes({ id: bm.id }) ? { supportsReferenceImages: false } : {}),
    // Wan 3.0: видео-референсы (до 5 роликов) — только пока за их секунды берём и мы (плата за секунды входного видео,
    // backend services/wan_refs, 26.10): без неё сервер ролик не примет, и канала в виджете нет вовсе.
    ...(isWan3Model({ id: bm.id }) ? { supportsReferenceVideo: !!bm.inputVideoBilling, maxReferenceVideos: 5 } : {}),
    // Kling 4.0 — то же правило (backend services/kling4_refs, 26.10): до 5 роликов, только при плате за секунды входа.
    ...(isKling4Model({ id: bm.id }) ? { supportsReferenceVideo: !!bm.inputVideoBilling, maxReferenceVideos: 5 } : {}),
    // Core identity — ПОСЛЕ всех спредов, чтобы ui_metadata/meta не
    // могли случайно перезаписать идентификатор модели.
    id: bm.id,
    provider: bm.adapter,
    backendModel: bm.id,
  }
}

/** Тянет провайдеров и каталог ОДИН раз на все вызовы: готовый ответ отдаёт
 *  кэш, а параллельные вызовы получают одно и то же обещание. Заполняет
 *  модульный кэш и ничего не знает про React — стейт раскладывает сам хук. */
function loadProviders(): Promise<void> {
  if (cachedBrands && cachedCatalog) return Promise.resolve()
  if (inflightProviders) return inflightProviders

  inflightProviders = Promise.all([
    fetch(`${API_BASE}/api/v1/providers?enabled_only=true`).then(r => r.json()),
    // Каталог необязателен: без него витрины живут на данных провайдеров —
    // его провал не роняет пару (поведение прежнее).
    fetch(`${API_BASE}/api/v1/admin/public/catalog`)
      .then(r => r.json())
      .catch(() => ({ ok: true, catalog: {}, filters: [], assignments: [], tools: [] })),
    // Возможности моделей ИЗ КОДА адаптеров. Кладутся САМЫМ НИЖНИМ слоем (см.
    // сборку модели ниже): таблица MODEL_CAPS и ui_metadata главнее, поэтому
    // ответ может только добавить параметры, о которых никто не знал —
    // у большинства моделей ui_metadata пуст, и виджет молчал про их настройки.
    // Провал ручки (старый бэк) ничего не меняет.
    fetch(`${API_BASE}/api/v1/providers/capabilities`)
      .then(r => r.json())
      .catch(() => ({ ok: false })),
  ])
    .then(([provData, catData, capsData]) => {
      if (capsData?.ok && capsData.capabilities) cachedCodeCaps = capsData.capabilities
      if (provData.ok) cachedBrands = groupByBrand(provData.providers)
      if (catData.ok && catData.catalog) cachedCatalog = catData.catalog
      if (catData.ok && catData.filters) cachedFilters = catData.filters
      if (catData.ok && catData.assignments) cachedAssignments = catData.assignments
      if (catData.ok && Array.isArray(catData.tools)) cachedTools = catData.tools
    })
    .finally(() => {
      // Успех — дальше отвечает кэш; провал — следующее монтирование повторит.
      inflightProviders = null
    })

  return inflightProviders
}

export function useProviders() {
  const [brands, setBrands] = useState<Brand[]>(cachedBrands || [])
  const [catalog, setCatalog] = useState<Record<string, CatalogEntry>>(cachedCatalog || {})
  const [apiFilters, setApiFilters] = useState<ApiFilter[]>(cachedFilters || [])
  const [apiAssignments, setApiAssignments] = useState<ApiAssignment[]>(cachedAssignments || [])
  const [apiTools, setApiTools] = useState<ApiTool[]>(cachedTools || [])
  const [loading, setLoading] = useState(!cachedBrands)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (cachedBrands && cachedCatalog) return
    // Отписка вместо AbortController: запрос теперь ОБЩИЙ, и отмена при
    // размонтировании одного потребителя обрывала бы его всем остальным.
    // Ушедший со страницы компонент просто не трогает свой стейт, а ответ
    // всё равно ложится в кэш — следующему монтированию уже не грузить.
    let alive = true

    loadProviders()
      .then(() => {
        if (!alive) return
        if (cachedBrands) setBrands(cachedBrands)
        else setError('Ошибка загрузки провайдеров')
        if (cachedCatalog) setCatalog(cachedCatalog)
        if (cachedFilters) setApiFilters(cachedFilters)
        if (cachedAssignments) setApiAssignments(cachedAssignments)
        if (cachedTools) setApiTools(cachedTools)
      })
      .catch(err => {
        if (alive) setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [])

  const allModels = useMemo(() => {
    const models: Model[] = []
    for (const brand of brands) {
      for (const bm of brand.models) {
        const model = brandModelToModel(brand, bm)
        const key = `${bm.adapter}:${bm.id}`
        const cat = catalog[key]
        if (cat) {
          if (cat.custom_description) model.description = cat.custom_description
          if (cat.icon_url) model.icon = cat.icon_url
        }
        // Порядок витрины кладём на сам объект: витрины склеивают модели с
        // карточками-инструментами и сортируют объединённый список по нему.
        model.showcaseOrder = cat?.sort_order ?? 9999
        models.push(model)
      }
    }
    models.sort((a, b) => {
      const keyA = `${a.provider}:${a.id}`
      const keyB = `${b.provider}:${b.id}`
      const sA = catalog[keyA]?.sort_order ?? 9999
      const sB = catalog[keyB]?.sort_order ?? 9999
      return sA - sB
    })
    return models
  }, [brands, catalog])

  const widgetModels = useMemo(() => {
    const models: Model[] = []
    for (const brand of brands) {
      for (const bm of brand.models) {
        const model = brandModelToModel(brand, bm)
        const key = `${bm.adapter}:${bm.id}`
        const cat = catalog[key]
        if (cat) {
          if (cat.is_visible_widget === false) continue
          if (cat.widget_description) model.description = cat.widget_description
          else if (cat.custom_description) model.description = cat.custom_description
          if (cat.icon_url) model.icon = cat.icon_url
        }
        model.showcaseOrder = cat?.widget_sort_order ?? 9999
        models.push(model)
      }
    }
    models.sort((a, b) => {
      const keyA = `${a.provider}:${a.id}`
      const keyB = `${b.provider}:${b.id}`
      const sA = catalog[keyA]?.widget_sort_order ?? 9999
      const sB = catalog[keyB]?.widget_sort_order ?? 9999
      return sA - sB
    })
    return models
  }, [brands, catalog])

  // Модели, видимые В КАТАЛОГЕ САЙТА. Глазик «Скрыта на странице» в админке
  // (model_catalog.is_visible_page) существовал, но не применялся нигде: у
  // моделей работал только виджетный близнец. Фильтруем ОТДЕЛЬНЫМ списком, а не
  // внутри allModels — по нему идут поиск модели из ссылки ?model=, подбор
  // видео-модели и проверка «модель исчезла из каталога». Скрытие из списка ≠
  // отключение: по прямой ссылке или из карточки модель обязана открываться.
  const pageModels = useMemo(
    () => allModels.filter((m) => catalog[`${m.provider}:${m.id}`]?.is_visible_page !== false),
    [allModels, catalog],
  )

  const pageFilters = useMemo(() => {
    return apiFilters.filter(f => f.is_visible_page !== false)
  }, [apiFilters])

  const widgetFilters = useMemo(() => {
    return apiFilters.filter(f => f.is_visible_widget !== false)
  }, [apiFilters])

  // ⚠️ Карточки-инструменты НАМЕРЕННО отдельными списками, а не подмешаны в
  // allModels/widgetModels: те массивы читают ~35 мест, включая пикеры моделей
  // генерации (GenerateInputBar, GenerateWidget, WidgetModelPicker, useSectionNav).
  // Не-генерируемая карточка там сломала бы генерацию. Витрины (ModelSelector,
  // useBotsCatalog) склеивают списки у себя.
  const pageTools = useMemo(
    () => apiTools
      .filter(t => t.is_visible_page !== false)
      .slice()
      .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999))
      .map(t => toolToModel(t, 'page')),
    [apiTools],
  )

  const widgetTools = useMemo(
    () => apiTools
      .filter(t => t.is_visible_widget !== false)
      .slice()
      .sort((a, b) => (a.widget_sort_order ?? 9999) - (b.widget_sort_order ?? 9999))
      .map(t => toolToModel(t, 'widget')),
    [apiTools],
  )

  return { brands, allModels, pageModels, widgetModels, loading, error, catalog, pageFilters, widgetFilters, apiAssignments, pageTools, widgetTools }
}